import { useGenerateCrmObjectsSearchQueryObject } from 'crm-object-search-query-utilities/crmObjectSearch';
import { useEffect, useMemo, useRef } from 'react';
import { DEFAULT_BOARD_PAGE_SIZE } from './data/constants';
import { useObjectTypeAppSettings } from 'framework-app-settings-features-lib';
import quickFetch from 'quick-fetch';
import { makeGqlEarlyRequest } from 'apollo-link-hub-http/quickFetch';
import { buildCardSectionRegistry, useLoadCardSectionRegistryMap } from 'crm-object-board-card/registry';
import { useLoadBoardCardSectionSettings, useLoadCardSectionProperties } from 'crm-object-board-card/hooks';
import { useCorePrefetch } from 'crm-object-board-core/prefetch';
import { getPropertiesForsearch } from './data/usePropertiesForSearch';
import { useGetHasGate, useGetHasScope } from 'crm-object-board-core/user';
import { quickFetchOptinAvailability } from 'ui-addon-opt/quick-fetch';
const CPQ_OPTIN_NAME = 'cpq-quote-private-beta';
export const usePrefetch = options => {
  const loaded = useRef(false);
  const {
    properties,
    objectTypeDefinition
  } = useCorePrefetch({
    objectTypeId: options.objectTypeId,
    pipelineId: options.pipelineId
  });
  const {
    data: tagSettingsMetadata
  } = useObjectTypeAppSettings(options.objectTypeId, 'crmTagsSettings');
  const properitesForSearch = useMemo(() => {
    return getPropertiesForsearch(objectTypeDefinition, [...(properties || []), tagSettingsMetadata ? tagSettingsMetadata.propertyName.value : null]);
  }, [properties, tagSettingsMetadata, objectTypeDefinition]);
  const {
    data: searchQuery,
    loading: searchQueryLoading
  } = useGenerateCrmObjectsSearchQueryObject({
    inputQuery: Object.assign({}, options.searchQuery, {
      filterGroups: options.searchQuery.filterGroups,
      properties: properitesForSearch,
      objectTypeId: options.objectTypeId
    })
  });
  useEffect(() => {
    const skip = searchQueryLoading || !properties || !tagSettingsMetadata || !objectTypeDefinition;
    if (skip || loaded.current) {
      return;
    }
    loaded.current = true;
    makeGqlEarlyRequest({
      url: quickFetch.getApiUrl('/graphql/crm'),
      operation: {
        operationName: 'CRM_OBJECT_BOARD_CRM_SEARCH_QUERY',
        query: importGql('CRM_OBJECT_BOARD_CRM_SEARCH_QUERY'),
        variables: Object.assign({}, searchQuery, {
          count: DEFAULT_BOARD_PAGE_SIZE,
          offset: 0,
          stageIds: options.stageIds
        })
      }
    });
  }, [searchQuery, searchQueryLoading, properties, tagSettingsMetadata, objectTypeDefinition, options.stageIds]);
};
export const usePrefetchV2 = options => {
  const loaded = useRef(false);
  quickFetchOptinAvailability(CPQ_OPTIN_NAME);
  const {
    objectTypeDefinition
  } = useCorePrefetch({
    objectTypeId: options.objectTypeId,
    pipelineId: options.pipelineId
  });
  const {
    loading: scopesLoading,
    hasScope
  } = useGetHasScope();
  const {
    loading: gatesLoading,
    hasGate
  } = useGetHasGate();
  const context = useMemo(() => {
    return {
      objectTypeId: options.objectTypeId,
      pipelineId: options.pipelineId,
      hasScope,
      hasGate,
      objectTypeDefinition
    };
  }, [options.objectTypeId, options.pipelineId, hasScope, hasGate, objectTypeDefinition]);
  const {
    data: cardSectionRegistryMap,
    loading: cardSectionRegistryMapLoading
  } = useLoadCardSectionRegistryMap({
    skip: scopesLoading || gatesLoading,
    appId: options.appId,
    objectTypeId: options.objectTypeId,
    hasScope,
    hasGate,
    context
  });
  const {
    data: cardSectionSettings,
    loading: cardSectionSettingsLoading
  } = useLoadBoardCardSectionSettings({
    pipelineId: options.pipelineId,
    objectTypeId: options.objectTypeId,
    appId: options.appId,
    skip: false
  });
  const registry = useMemo(() => {
    return buildCardSectionRegistry(cardSectionRegistryMap);
  }, [cardSectionRegistryMap]);
  const {
    data: cardSectionProperties,
    loading: cardSectionPropertiesLoading
  } = useLoadCardSectionProperties({
    cardSectionSettingsLoading,
    cardSectionSettings: cardSectionSettings || [],
    registry,
    registryLoading: cardSectionRegistryMapLoading
  });
  const {
    data: tagSettingsMetadata
  } = useObjectTypeAppSettings(options.objectTypeId, 'crmTagsSettings');
  const {
    data: searchQuery,
    loading: searchQueryLoading
  } = useGenerateCrmObjectsSearchQueryObject({
    inputQuery: Object.assign({}, options.searchQuery, {
      filterGroups: options.searchQuery.filterGroups,
      properties: cardSectionProperties,
      objectTypeId: options.objectTypeId
    })
  });
  useEffect(() => {
    const skip = searchQueryLoading || !tagSettingsMetadata || cardSectionPropertiesLoading || cardSectionSettingsLoading || cardSectionRegistryMapLoading;
    if (skip || loaded.current) {
      return;
    }
    loaded.current = true;
    makeGqlEarlyRequest({
      url: quickFetch.getApiUrl('/graphql/crm'),
      operation: {
        operationName: 'CRM_OBJECT_BOARD_CRM_SEARCH_QUERY',
        query: importGql('CRM_OBJECT_BOARD_CRM_SEARCH_QUERY'),
        variables: Object.assign({}, searchQuery, {
          count: DEFAULT_BOARD_PAGE_SIZE,
          offset: 0,
          stageIds: options.stageIds
        })
      }
    });
  }, [searchQuery, searchQueryLoading, tagSettingsMetadata, cardSectionPropertiesLoading, cardSectionSettingsLoading, cardSectionRegistryMapLoading, options.stageIds]);
};